import { useCallback, useEffect, useRef, useState } from 'react';

import { logWarn } from '../lib/logger';
import { supabase } from '../lib/supabase';
import { messageOf, oneRow } from '../store/crm/writeResult';
import { invoiceCategoryPatch } from '../utils/revenue';

/** A manual Stripe invoice as a lead's sheet shows it. */
export interface LeadInvoice {
  id: string;
  status: string;
  amount_due: number;
  amount_paid: number;
  invoice_date: string | null;
  description: string | null;
  hosted_invoice_url: string | null;
  is_one_time: boolean | null;
  revenue_category: string | null;
  lead_id: string | null;
}

const FIELDS = 'id, status, amount_due, amount_paid, invoice_date, description, hosted_invoice_url, is_one_time, revenue_category, lead_id';

/**
 * A lead's manual invoices (PLAN-ENCRE-OUTILS O3 bis) — the only place they are tied to a lead,
 * and the invoice MRR counts them by `stripe_invoices.lead_id`: those tied to it, and those of its
 * Stripe customer not tied to anyone yet (to tie with one gesture); on demand, the unlinked ones of
 * any customer to pick from. Each write checks its row (`oneRow`) and says its failure.
 */
export function useLeadInvoices(leadId: string | null, customerId: string | null) {
  const [invoices, setInvoices] = useState<LeadInvoice[]>([]);
  const [ready, setReady] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const latest = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!leadId) return;
    const id = leadId;
    const manual = () => supabase.from('stripe_invoices').select(FIELDS).eq('billing_reason', 'manual');
    // Two reads, never the customer's id pasted into a filter string.
    const [tied, ofCustomer] = await Promise.all([
      manual().eq('lead_id', id).order('invoice_date', { ascending: false }).limit(20),
      customerId ? manual().eq('customer_id', customerId).is('lead_id', null).order('invoice_date', { ascending: false }).limit(20) : Promise.resolve({ data: [], error: null }),
    ]);
    if (latest.current !== id) return;
    const failed = tied.error ?? ofCustomer.error;
    if (failed) {
      logWarn('crm', 'lead sheet: invoices unreadable', { leadId: id, error: failed.message });
      setReadError(failed.message);
      return;
    }
    setReadError(null);
    // One row per invoice, whichever read brought it.
    const byId = new Map([...(tied.data ?? []), ...(ofCustomer.data ?? [])].map((i) => [i.id, i]));
    setInvoices([...byId.values()].sort((a, b) => (b.invoice_date ?? '').localeCompare(a.invoice_date ?? '')));
    setReady(true);
  }, [leadId, customerId]);

  useEffect(() => {
    latest.current = leadId;
    setInvoices([]);
    setReady(false);
    setReadError(null);
    setWriteError(null);
    void load();
  }, [leadId, load]);

  /**
   * One invoice written — only if it is still held as read (`holder`): tied meanwhile to another
   * lead, it is not taken from it; the write touches no row and says so.
   */
  const write = useCallback(
    async (invoiceId: string, patch: Record<string, unknown>, holder: string | null) => {
      setBusy(invoiceId);
      try {
        const query = supabase.from('stripe_invoices').update({ ...patch, updated_at: new Date().toISOString() }).eq('id', invoiceId);
        await oneRow((holder ? query.eq('lead_id', holder) : query.is('lead_id', null)).select('id'), 'La facture');
        setWriteError(null);
      } catch (error: unknown) {
        setWriteError(messageOf(error, 'Refus sans message.'));
      }
      setBusy(null);
      await load();
    },
    [load],
  );

  /** The unlinked manual invoices of any customer, to tie one to this lead. */
  const listUnlinked = useCallback(async (): Promise<{ invoices: LeadInvoice[]; error: string | null }> => {
    const { data, error } = await supabase
      .from('stripe_invoices')
      .select(FIELDS)
      .eq('billing_reason', 'manual')
      .is('lead_id', null)
      .order('invoice_date', { ascending: false })
      .limit(30);
    return { invoices: data ?? [], error: error?.message ?? null };
  }, []);

  return {
    invoices,
    ready,
    readError,
    writeError,
    busy,
    link: (invoiceId: string) => (leadId ? write(invoiceId, { lead_id: leadId }, null) : Promise.resolve()),
    unlink: (invoiceId: string) => (leadId ? write(invoiceId, { lead_id: null }, leadId) : Promise.resolve()),
    setCategory: (invoiceId: string, category: string) => (leadId ? write(invoiceId, invoiceCategoryPatch(category), leadId) : Promise.resolve()),
    listUnlinked,
  };
}
